<template>
    <div class="relative" ref="rootRef">
        <button
            type="button"
            @click="open = !open"
            class="inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-hu-green/20 focus:ring-offset-1"
            :aria-expanded="open"
            :aria-label="currentLang?.translated?.name || currentLang?.name ? ('Language: ' + (currentLang.translated?.name || currentLang.name)) : 'Select language'"
            aria-haspopup="listbox"
            aria-controls="header-lang-menu"
        >
            <img
                v-if="currentLang?.flag"
                :src="flagSrc(currentLang.flag)"
                alt=""
                class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                width="20"
                height="20"
            />
<!--            <span>{{ currentLang?.code_view ?? 'EN' }}</span>-->
            <svg
                class="h-4 w-4 text-gray-500 transition-transform duration-200"
                :class="{ 'rotate-180': open }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-show="open"
                id="header-lang-menu"
                role="listbox"
                class="absolute right-0 z-50 mt-2 w-full origin-top-right rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5 min-w-[28rem]"
            >
                <div class="grid grid-cols-2 gap-x-1 gap-y-0.5">
                    <a
                        v-for="lang in visibleLanguages"
                        :key="lang.code"
                        :href="lang.path || '#'"
                        class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 rounded-lg"
                        :class="currentLang?.code === lang.code ? 'bg-hu-green/5 text-hu-green font-semibold' : ''"
                        @click="persistCurrencyForLanguage(lang)"
                    >
                        <img
                            v-if="lang.flag"
                            :src="flagSrc(lang.flag)"
                            alt=""
                            class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                            width="20"
                            height="20"
                        />
                        <span
                            v-else
                            class="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0"
                        >
                            {{ (lang.code || '').substring(0, 2).toUpperCase() }}
                        </span>
                        <span class="truncate">{{ lang.name }}</span>
                    </a>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import ApiClient from '../core/ApiClient.js';
import { flagSrc } from '../utils/flagSrc.js';

function normalizeLanguagesResponse(res) {
    const body = res?.data;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body)) return body;
    return [];
}

const LANGUAGE_CURRENCY_BY_CODE = {
    bg: 'BGN',
    cs: 'CZK',
    cz: 'CZK',
    de: 'EUR',
    en: 'GBP',
    es: 'EUR',
    fr: 'EUR',
    hr: 'EUR',
    hu: 'HUF',
    it: 'EUR',
    nl: 'EUR',
    pl: 'PLN',
    ro: 'RON',
    sk: 'EUR',
    sl: 'EUR',
};

function availableCurrencyCodes() {
    const list = typeof window !== 'undefined' && Array.isArray(window.__APP_DATA__?.currencies)
        ? window.__APP_DATA__.currencies
        : [];
    return new Set(list.map((c) => String(c?.code || '').trim().toUpperCase()).filter(Boolean));
}

function currencyCodeForLanguage(lang) {
    const direct = String(
        lang?.currency?.code
        ?? lang?.default_currency?.code
        ?? lang?.currency_code
        ?? lang?.currencyCode
        ?? lang?.default_currency_code
        ?? lang?.defaultCurrencyCode
        ?? '',
    ).trim().toUpperCase();
    if (direct) return direct;

    const langCode = String(lang?.code || '').trim().toLowerCase().split(/[-_]/)[0];
    return LANGUAGE_CURRENCY_BY_CODE[langCode] || '';
}


export default {
    name: 'HeaderLanguageSelector',
    props: {
        currentLang: { type: Object, default: null },
        languages: { type: Array, default: () => [] },
        pathsByLanguage: { type: Object, default: () => ({}) },
    },
    setup(props) {
        const open = ref(false);
        const rootRef = ref(null);
        /** null = use SSR `languages` prop; set after successful /languages API */
        const languagesFromApi = ref(null);

        const resolvedLanguages = computed(() => {
            const fromApi = languagesFromApi.value;
            if (Array.isArray(fromApi) && fromApi.length) return fromApi;
            return props.languages || [];
        });

        const visibleLanguages = computed(() => {
            const paths = props.pathsByLanguage || {};
            const langs = resolvedLanguages.value;

            const langMap = Object.fromEntries(
                langs.map((l) => [l.code, l])
            );
            const langByUpper = Object.fromEntries(
                langs
                    .filter((l) => l && l.code != null && l.code !== '')
                    .map((l) => [String(l.code).toUpperCase(), l])
            );

            return Object.entries(paths)
                .map(([code, path]) => {
                    const lang =
                        langMap[code]
                        ?? langByUpper[String(code).toUpperCase()];

                    return {
                        ...(lang || {}),
                        code,
                        path,
                        name:
                            lang?.translated?.name
                            ?? lang?.code_view
                            ?? code
                    };
                })
                .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        });

        function onOutsideClick(e) {
            if (rootRef.value && !rootRef.value.contains(e.target)) {
                open.value = false;
            }
        }

        function persistCurrencyForLanguage(lang) {
            if (typeof window === 'undefined' || !window.localStorage) return;
            const code = currencyCodeForLanguage(lang);
            if (!code) return;

            const available = availableCurrencyCodes();
            if (available.size && !available.has(code)) return;
            localStorage.setItem('currency', code);
            const langCode = String(lang?.code || '').trim().toLowerCase().split(/[-_]/)[0];
            if (langCode) localStorage.setItem('currencyLang', langCode);
        }

        onMounted(async () => {
            document.addEventListener('click', onOutsideClick);
            try {
                const api = new ApiClient();
                const res = await api.request({ path: '/languages' });
                const list = normalizeLanguagesResponse(res);
                if (list.length) {
                    languagesFromApi.value = list.map((lang) => (
                        lang?.flag ? { ...lang, flag: flagSrc(lang.flag) } : lang
                    ));
                }
            } catch {
                /* keep SSR languages */
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', onOutsideClick);
        });

        return { open, rootRef, visibleLanguages, persistCurrencyForLanguage, flagSrc };
    },
};

</script>
